import { Controller } from "@hotwired/stimulus"

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`

const FRAG = `
precision highp float;

uniform vec2  u_res;
uniform float u_time;

float hash(vec2 p) {
  p = fract(p * vec2(127.1, 311.7));
  p += dot(p, p + 74.3);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i),               hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0,1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 6; i++) { v += a * noise(p); p *= 2.1; a *= 0.5; }
  return v;
}

// A single drifting cloud. Offset seeds each cloud's unique position and speed.
float cloud(vec2 uv, float seed, float t, float aspect) {
  // Each cloud drifts at a slightly different speed and starts at a unique x.
  float speed = 0.018 + seed * 0.009;
  float baseX = fract(seed * 0.37 + t * speed);
  float baseY = 0.55 + seed * 0.14;

  vec2 center = vec2(baseX, baseY);
  // Wrap horizontally so clouds re-enter from the left.
  vec2 d = uv - center;
  if (d.x >  0.5) d.x -= 1.0;
  if (d.x < -0.5) d.x += 1.0;
  // Convert to screen-proportional space then squash vertically so clouds
  // are wide and flat (~3:1) rather than circular or tall.
  d.x *= aspect;
  d.y *= 2.5;

  float r = length(d);

  // Slow-drifting fbm noise gives each cloud a puffed, irregular silhouette.
  vec2 nq = uv * 5.0 + vec2(t * speed * 3.0 + seed * 10.0, seed * 7.0);
  float n = fbm(nq) * 0.18;

  float edge = 0.12 + n;
  return smoothstep(edge, edge - 0.04, r);
}

void main() {
  float aspect = u_res.x / u_res.y;
  vec2  uv = gl_FragCoord.xy / u_res;   // 0..1, y up
  float t  = u_time;

  // Sky gradient: warm pale blue at horizon, rich blue overhead.
  vec3 skyLow  = vec3(0.74, 0.88, 0.98);
  vec3 skyHigh = vec3(0.33, 0.60, 0.90);
  vec3 col = mix(skyLow, skyHigh, smoothstep(0.0, 1.0, uv.y));

  // Sun — off-screen to the right; only its halo bleeds into view.
  vec2 sunPos = vec2(1.22, 0.80);
  float sunDist = length(uv - sunPos);
  col += vec3(1.0, 0.92, 0.60) * 0.22 * smoothstep(0.90, 0.0, sunDist);
  col += vec3(1.0, 0.96, 0.75) * 0.18 * smoothstep(0.45, 0.0, sunDist);

  // Out-of-focus rolling ground.
  // hy is the local horizon height (varies gently with x, drifts very slowly).
  float hy = 0.19 + fbm(vec2(uv.x * 2.2 + t * 0.004, 0.0)) * 0.09;
  // Wide smoothstep simulates the soft bokeh of a distant landscape.
  float dof    = 0.045;
  float groundA = smoothstep(hy + dof, hy - dof, uv.y);
  // Colour: richer green low, fades to hazy pale green near horizon.
  float hFade   = clamp(uv.y / (hy + 0.06), 0.0, 1.0);
  vec3 groundNear = vec3(0.50, 0.63, 0.36);
  vec3 groundFar  = vec3(0.66, 0.73, 0.54);
  vec3 groundCol  = mix(groundNear, groundFar, hFade);
  // Blurry dark patches — distant hedgerows / tree lines near the horizon.
  float patches   = fbm(vec2(uv.x * 3.8 + t * 0.003, 4.1));
  float patchMask = smoothstep(hy - 0.03, hy + 0.04, uv.y); // only near horizon
  groundCol = mix(groundCol, groundCol * 0.62, patches * 0.45 * patchMask);
  // Atmospheric haze blends ground into sky colour at the horizon line.
  groundCol = mix(groundCol, skyLow * 0.92, hFade * 0.55);
  col = mix(col, groundCol, groundA);

  // Clouds — 6 independent drifters at varying heights / speeds.
  for (int i = 0; i < 6; i++) {
    float seed = float(i) * 1.618;
    float c = cloud(uv, seed, t, aspect);
    float warmth = smoothstep(0.8, 0.0, length(uv - sunPos));
    vec3 cloudCol = mix(vec3(0.97, 0.97, 0.98), vec3(1.0, 0.97, 0.88), warmth);
    float belly = smoothstep(0.0, 0.06, uv.y - (0.55 + float(i) * 0.14));
    cloudCol = mix(cloudCol * 0.82, cloudCol, belly);
    col = mix(col, cloudCol, c * 0.92);
  }

  gl_FragColor = vec4(col, 1.0);
}
`

export default class extends Controller {
  connect() {
    const gl = this.element.getContext("webgl") || this.element.getContext("experimental-webgl")
    if (!gl) return
    this._gl = gl

    this._prog = this._buildProg(gl)
    if (!this._prog) return
    gl.useProgram(this._prog)
    this._setupGeo(gl)

    this._u = {
      res:  gl.getUniformLocation(this._prog, "u_res"),
      time: gl.getUniformLocation(this._prog, "u_time"),
    }

    this._setSize()

    this._reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    this._start   = performance.now()

    this._running = true
    this._ro = new ResizeObserver(() => this._setSize())
    this._ro.observe(document.documentElement)
    this._tick()
  }

  disconnect() {
    this._running = false
    this._ro?.disconnect()
    const gl = this._gl
    if (gl) {
      if (this._prog) gl.deleteProgram(this._prog)
      if (this._buf)  gl.deleteBuffer(this._buf)
    }
  }

  _setSize() {
    const c = this.element
    c.width  = window.innerWidth
    c.height = window.innerHeight
    const gl = this._gl
    if (!gl) return
    gl.viewport(0, 0, c.width, c.height)
    gl.useProgram(this._prog)
    gl.uniform2f(this._u.res, c.width, c.height)
  }

  _tick() {
    if (!this._running) return
    const gl = this._gl
    const t  = this._reduced ? 0 : (performance.now() - this._start) / 1000
    gl.useProgram(this._prog)
    gl.uniform1f(this._u.time, t)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
    if (this._reduced) { this._running = false; return }
    requestAnimationFrame(() => this._tick())
  }

  _buildProg(gl) {
    const vs = this._compile(gl, gl.VERTEX_SHADER,   VERT)
    const fs = this._compile(gl, gl.FRAGMENT_SHADER, FRAG)
    if (!vs || !fs) return null
    const p = gl.createProgram()
    gl.attachShader(p, vs); gl.attachShader(p, fs)
    gl.linkProgram(p)
    gl.deleteShader(vs); gl.deleteShader(fs)
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.error("Sky shader link error:", gl.getProgramInfoLog(p))
      return null
    }
    return p
  }

  _compile(gl, type, src) {
    const s = gl.createShader(type)
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error("Sky shader compile error:", gl.getShaderInfoLog(s))
      gl.deleteShader(s); return null
    }
    return s
  }

  _setupGeo(gl) {
    this._buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this._buf)
    gl.bufferData(gl.ARRAY_BUFFER,
      new Float32Array([-1,-1, 1,-1, -1,1,  -1,1, 1,-1, 1,1]),
      gl.STATIC_DRAW)
    const loc = gl.getAttribLocation(this._prog, "a_pos")
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
  }
}
